import { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';

export const useUserLocation = () => {
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Try to get cached location from localStorage on mount
  useEffect(() => {
    const cached = localStorage.getItem('userLocation');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        // Only use if less than 1 hour old
        if (parsed.timestamp && Date.now() - parsed.timestamp < 3600000) {
          setLocation({ lat: parsed.lat, lng: parsed.lng });
        }
      } catch (e) {
        localStorage.removeItem('userLocation');
      }
    }
  }, []);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setLocation(newLocation);
        setLoading(false);
        
        // Cache location
        localStorage.setItem('userLocation', JSON.stringify({
          ...newLocation,
          timestamp: Date.now()
        }));
        
        toast.success('Location found!');
      },
      (err) => {
        setError(err.message);
        setLoading(false);
        toast.error('Unable to get your location. Please check your browser settings.');
      },
      { 
        enableHighAccuracy: true, 
        timeout: 10000,
        maximumAge: 300000 // Cache for 5 minutes
      }
    );
  }, []);

  const clearLocation = useCallback(() => {
    setLocation(null);
    localStorage.removeItem('userLocation');
  }, []);

  return {
    location,
    loading,
    error,
    requestLocation,
    clearLocation,
    hasLocation: !!location
  };
};

export default useUserLocation;
